import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, GripVertical, CalendarDays, Trash2, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMap = {
  draft: { label: "Entwurf", cls: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200" },
  review: { label: "In Pruefung", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200" },
  approved: { label: "Freigegeben", cls: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  scheduled: { label: "Geplant", cls: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  published: { label: "Veroeffentlicht", cls: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
};

const platformColors = {
  instagram: "#DB2777",
  facebook: "#1877F2",
  tiktok: "#0F172A",
  linkedin: "#0A66C2",
};
const platformLabels = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", linkedin: "LinkedIn", pinterest: "Pinterest" };

const monthNames = ["Januar", "Februar", "Maerz", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function CalendarPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [content, setContent] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  const [dragItem, setDragItem] = useState(null);
  const [detail, setDetail] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const navigate = useNavigate();
  const { t } = useLanguage();

  const loadContent = () => {
    axios.get(`${API}/content`, { withCredentials: true })
      .then((r) => setContent(r.data))
      .catch(console.error);
  };

  useEffect(() => {
    loadContent();
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data)).catch(console.error);
    axios.get(`${API}/campaigns`, { withCredentials: true })
      .then((r) => setCampaigns(Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => {});
  }, []);

  const openDetail = (item) => {
    setDetail(item);
    const [d, tm] = (item.schedule_date || "").split("T");
    setEditDate(d || "");
    setEditTime((tm || "09:00").slice(0, 5));
  };

  const saveSchedule = async () => {
    if (!editDate) { toast.info("Bitte Datum waehlen"); return; }
    try {
      await axios.put(`${API}/content/${detail.id}`, { schedule_date: `${editDate}T${editTime || "09:00"}` }, { withCredentials: true });
      toast.success("Veroeffentlichung geaendert");
      setDetail(null);
      loadContent();
    } catch (e) { toast.error(e.response?.data?.detail || "Aendern fehlgeschlagen"); }
  };

  const deleteItem = async () => {
    if (!window.confirm("Diesen geplanten Inhalt wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/content/${detail.id}`, { withCredentials: true });
      toast.success("Geloescht");
      setDetail(null);
      loadContent();
    } catch (e) { toast.error(e.response?.data?.detail || "Loeschen fehlgeschlagen"); }
  };

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const startOffset = (firstDayOfMonth + 6) % 7;

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1);
  };

  const getContentForDate = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return content.filter((c) => {
      if (!c.schedule_date) return false;
      const matchesDate = c.schedule_date.startsWith(dateStr);
      const matchesCompany = filterCompany === "all" || c.company_id === filterCompany;
      return matchesDate && matchesCompany;
    });
  };

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleDragStart = (item) => {
    setDragItem(item);
  };

  const handleDrop = async (day) => {
    if (!dragItem) return;
    const newDateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const existingTime = dragItem.schedule_date?.split("T")[1] || "09:00";
    const newScheduleDate = `${newDateStr}T${existingTime}`;
    try {
      await axios.put(`${API}/content/${dragItem.id}`, { schedule_date: newScheduleDate }, { withCredentials: true });
      toast.success("Verschoben");
      loadContent();
    } catch (e) {
      toast.error("Verschieben fehlgeschlagen");
    }
    setDragItem(null);
  };

  return (
    <div data-testid="calendar-page" className="space-y-4 sm:space-y-6 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">{t("calendar.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1 text-sm">{t("calendar.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-full sm:w-48" data-testid="calendar-company-filter">
              <SelectValue placeholder={t("general.all_companies")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("general.all_companies")}</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => {
              const q = filterCompany !== "all" ? `?company_id=${filterCompany}` : "";
              const url = `${API}/calendar/export.ics${q}`;
              window.open(url, "_blank");
            }}
            data-testid="export-ical-btn"
            className="whitespace-nowrap"
          >
            <CalendarDays className="w-4 h-4 mr-1" /> iCal
          </Button>
        </div>
      </div>

      {/* Month Nav */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg sm:text-xl font-bold font-heading min-w-[180px] text-center">
          {monthNames[month - 1]} {year}
        </h2>
        <Button variant="outline" size="icon" onClick={nextMonth} data-testid="next-month-btn">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* DnD Hint */}
      <p className="text-xs text-[var(--studio-text-muted)] flex items-center gap-1">
        <GripVertical className="w-3 h-3" /> Inhalte per Drag & Drop zwischen Tagen verschieben
      </p>

      {/* Calendar */}
      <Card className="border-[var(--studio-border)] shadow-none overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-7 bg-[var(--studio-bg)]">
            {dayNames.map((d) => (
              <div key={d} className="p-2 sm:p-3 text-[10px] sm:text-xs font-semibold text-[var(--studio-text-muted)] text-center border-b border-[var(--studio-border)]">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, idx) => {
              const dayItems = day ? getContentForDate(day) : [];
              const isToday = day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
              return (
                <div
                  key={idx}
                  onDragOver={(e) => { if (day) e.preventDefault(); }}
                  onDrop={() => day && handleDrop(day)}
                  className={`min-h-[70px] sm:min-h-[100px] p-1 sm:p-2 border-b border-r border-[var(--studio-border)] ${
                    !day ? "bg-[var(--studio-bg)]/50" : "bg-[var(--studio-surface)]"
                  } ${isToday ? "ring-1 ring-inset ring-[#2563EB]" : ""}`}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <p className={`text-[10px] sm:text-xs font-semibold mb-1 ${isToday ? "text-[#2563EB]" : "text-[var(--studio-text-muted)]"}`}>
                        {day}
                      </p>
                      <div className="space-y-0.5 sm:space-y-1">
                        {dayItems.slice(0, 3).map((item) => {
                          const st = statusMap[item.status] || statusMap.draft;
                          return (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={() => handleDragStart(item)}
                              onClick={() => openDetail(item)}
                              className={`text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded truncate cursor-pointer hover:ring-1 hover:ring-[#2563EB] ${st.cls}`}
                              style={{ borderLeft: `3px solid ${platformColors[item.platform] || "#94A3B8"}` }}
                              title={`${platformLabels[item.platform] || item.platform || ""} — ${item.selected_content?.caption || item.format_type}`}
                              data-testid={`calendar-content-${item.id}`}
                            >
                              {item.format_type}
                            </div>
                          );
                        })}
                        {dayItems.length > 3 && (
                          <span className="text-[8px] sm:text-[10px] text-[var(--studio-text-muted)]">+{dayItems.length - 3}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Detail-Dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="calendar-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: platformColors[detail.platform] || "#94A3B8" }} />
                  {detail.format_type || "Beitrag"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><p className="text-xs text-[var(--studio-text-muted)]">Firma</p>
                  <p className="font-medium" data-testid="detail-company">{companies.find((c) => c.id === detail.company_id)?.name || "—"}</p></div>
                <div><p className="text-xs text-[var(--studio-text-muted)]">Plattform</p>
                  <p className="font-medium">{platformLabels[detail.platform] || detail.platform || "—"}</p></div>
                <div><p className="text-xs text-[var(--studio-text-muted)]">Freigabestatus</p>
                  <Badge className={(statusMap[detail.status] || statusMap.draft).cls} data-testid="detail-status">
                    {(statusMap[detail.status] || statusMap.draft).label}
                  </Badge></div>
                <div><p className="text-xs text-[var(--studio-text-muted)]">Kampagne</p>
                  <p className="font-medium">{campaigns.find((k) => k.id === detail.campaign_id)?.name || "Keine"}</p></div>
              </div>
              {detail.selected_content?.caption && (
                <div className="text-xs bg-[var(--studio-bg)] border border-[var(--studio-border)] rounded p-2 max-h-28 overflow-auto whitespace-pre-wrap" data-testid="detail-caption">
                  {detail.selected_content.caption}
                </div>
              )}
              <div>
                <p className="text-xs text-[var(--studio-text-muted)] mb-1">Veroeffentlichung aendern</p>
                <div className="flex items-center gap-2">
                  <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="h-8 text-xs w-36" data-testid="detail-date-input" />
                  <Input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} className="h-8 text-xs w-24" data-testid="detail-time-input" />
                  <Button size="sm" className="h-8 text-xs bg-[#2563EB] hover:bg-[#1D4ED8] text-white" onClick={saveSchedule} data-testid="detail-save-schedule">
                    Speichern
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[var(--studio-border)]">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate("/freigabe")} data-testid="detail-open-approval">
                  <ExternalLink className="w-3 h-3 mr-1" /> In Freigabe oeffnen / bearbeiten
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={deleteItem} data-testid="detail-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Loeschen
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(statusMap).map(([key, { label, cls }]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${cls}`} />
            <span className="text-[var(--studio-text-muted)]">{label}</span>
          </div>
        ))}
        <span className="text-[var(--studio-text-muted)]">·</span>
        {Object.entries(platformColors).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1.5" data-testid={`legend-platform-${key}`}>
            <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
            <span className="text-[var(--studio-text-muted)]">{platformLabels[key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
